import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Heart } from "lucide-react";
import { PageTitle } from "@/components/SettingsPanel";
import { EventCard } from "@/components/EventCard";
import { SpotCard } from "@/components/SpotCard";
import type { Spot } from "@/lib/spots";
import { api } from "@/lib/api";
import { usePrefs } from "@/contexts/PrefsContext";
import { useAuth } from "@/contexts/AuthContext";
import { useSections } from "@/lib/sections";
import type { AppEvent, User } from "@/types";

const Empty = ({ text }: { text: string }) => {
  const { t } = usePrefs();
  return (
    <div data-testid="favorites-empty" className="text-center py-16 space-y-4">
      <div className="mx-auto h-20 w-20 rounded-[1.75rem] bg-primary/10 text-primary grid place-items-center -rotate-6"><Heart className="h-10 w-10" /></div>
      <p className="text-muted-foreground max-w-xs mx-auto">{text}</p>
      <Link to="/app" data-testid="favorites-explore-link" className="inline-flex h-11 px-6 items-center rounded-full bg-primary text-primary-foreground font-bold">{t("hero.cta")}</Link>
    </div>
  );
};

export default function CustomerFavorites() {
  const { t } = usePrefs();
  const user = useAuth().user as User;
  const sec = useSections();
  const [pick, setTab] = useState<"events" | "spots">("events");
  const tab = !sec.events ? "spots" : !sec.spots && pick === "spots" ? "events" : pick;
  const [spots, setSpots] = useState<Spot[] | null>(null);
  const favS = user.fav_spots || [];
  const [events, setEvents] = useState<AppEvent[] | null>(null);
  const favE = user.fav_events || [];

  useEffect(() => {
    api.get("/favorites/events").then((r) => setEvents(r.data)).catch(() => setEvents([]));
  }, [favE.join(",")]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    api.get("/me/fav-spots").then((r) => setSpots(r.data)).catch(() => setSpots([]));
  }, [favS.join(",")]); // eslint-disable-line react-hooks/exhaustive-deps
  const shownS = spots?.filter((s) => favS.includes(s.spot_id)) ?? null;
  const shownE = events?.filter((e) => favE.includes(e.event_id)) ?? null;
  const tabBtn = (k: "events" | "spots", label: string, n?: number) => (
    <button data-testid={`favorites-tab-${k}`} onClick={() => setTab(k)} aria-pressed={tab === k}
      className={`h-10 px-5 rounded-full text-sm font-bold transition-[background-color,color] ${tab === k ? "bg-foreground text-background" : "bg-card border hover:bg-accent"}`}>
      {label}{n ? ` · ${n}` : ""}
    </button>
  );

  return (
    <div data-testid="customer-favorites-page">
      <PageTitle title={t("fav.title")} sub={t("fav.sub")} />
      <div className="flex gap-2 mb-5 overflow-x-auto no-scrollbar">{sec.events && tabBtn("events", t("fav.tabEvents"), shownE?.length)}{sec.spots && tabBtn("spots", t("fav.tabSpots"), shownS?.length)}</div>
      {tab === "spots" ? (
        shownS?.length === 0 ? <Empty text={t("fav.emptySpots")} /> : (
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">{shownS?.map((s, i) => <SpotCard key={s.spot_id} s={s} index={i} />)}</div>
        )
      ) : shownE?.length === 0 ? <Empty text={t("fav.emptyEvents")} /> : (
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">{shownE?.map((e, i) => <EventCard key={e.event_id} e={e} index={i} />)}</div>
      )}
    </div>
  );
}
